/**
 * Chat Message Service
 * Handles message operations: sending, fetching, attachments
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/services/logger';
import { chatRateLimiter } from '@/lib/services/chatRateLimiter';
import { validateMessageContent, validateRoomId, mightContainPII } from '@/lib/services/chatValidator';
import { safeRandomUUID } from '@/lib/utils/safeUuid';
import { config } from '@/lib/config/environment';
import { validateImageSecurity } from '@/lib/utils/imageSecurityValidator';
import type { Database } from '@/integrations/supabase/types';

type ChatMessage = Database['public']['Tables']['chat_messages']['Row'];

export interface ChatMessageWithSender extends ChatMessage {
  senderName?: string;
  senderAvatar?: string;
  isRead?: boolean;
}

// File validation constants from environment config
export const MAX_FILE_SIZE = config.uploads.chat.maxFileSize;
const ALLOWED_MIME_TYPES = [
  // Images
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];
const BLOCKED_EXTENSIONS = [
  'exe', 'bat', 'cmd', 'sh', 'msi', 'dll', 'com', 'scr', 'ps1', 'vbs', 'js', 'jar', 'app', 'dmg', 'pkg', 'deb', 'rpm'
];

export const chatMessageService = {
  /**
   * Get messages for a chat room with pagination
   */
  async getChatMessages(
    roomId: string,
    limit = 50,
    before?: string
  ): Promise<{ data: ChatMessageWithSender[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_room_id', roomId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (before) {
        query = query.lt('created_at', before);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Reverse to get chronological order
      return { data: (data || []).reverse(), error: null };
    } catch (error) {
      logger.error('Error fetching chat messages', {
        context: { feature: 'chat', function_name: 'getChatMessages' },
        error: error as Error,
        error_code: 'chat_messages_fetch_failed',
        error_type: 'unexpected',
      });
      return { data: null, error: error as Error };
    }
  },

  /**
   * Send a message to a chat room
   * Includes validation and rate limiting
   */
  async sendMessage(
    userId: string,
    roomId: string,
    content: string,
    contentType = 'text'
  ): Promise<{ data: ChatMessage | null; error: Error | null }> {
    try {
      if (!userId) {
        return { data: null, error: new Error('User not authenticated') };
      }

      // Validate room ID
      if (!validateRoomId(roomId)) {
        return { data: null, error: new Error('Неверный идентификатор чата') };
      }

      // Validate message content
      const validation = validateMessageContent(content, contentType);
      if (!validation.valid) {
        return { data: null, error: new Error(validation.error) };
      }

      // Check rate limits
      const rateLimit = chatRateLimiter.checkLimit(userId, roomId);
      if (!rateLimit.allowed) {
        return { data: null, error: new Error(rateLimit.reason || 'Превышен лимит сообщений') };
      }

      // Log warning if message might contain PII (but allow sending)
      if (contentType === 'text' && mightContainPII(content)) {
        logger.info('Chat message may contain PII', {
          context: { feature: 'chat', function_name: 'sendMessage' },
        });
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          chat_room_id: roomId,
          sender_user_id: userId,
          content: validation.sanitizedContent || content,
          content_type: contentType,
          content_type_codifier_key: 'chat_message_content_type',
        })
        .select()
        .single();

      if (error) throw error;

      // Record successful message for rate limiting
      chatRateLimiter.recordMessage(userId, roomId);

      // Update room's last_message_at
      await supabase
        .from('chat_rooms')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', roomId);

      return { data, error: null };
    } catch (error) {
      logger.error('Error sending message', {
        context: { feature: 'chat', function_name: 'sendMessage' },
        error: error as Error,
        error_code: 'message_send_failed',
        error_type: 'unexpected',
      });
      return { data: null, error: error as Error };
    }
  },

  /**
   * Validate file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `Размер файла превышает 10 МБ` };
    }

    // Check extension
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (BLOCKED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: `Файлы с расширением .${ext} запрещены` };
    }

    // Check MIME type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return { valid: false, error: `Тип файла не поддерживается. Разрешены: изображения, PDF, документы` };
    }

    return { valid: true };
  },

  /**
   * Upload a file attachment for a chat message
   * Returns the signed URL for the uploaded file (valid for 1 year)
   */
  async uploadAttachment(
    userId: string,
    roomId: string,
    file: File
  ): Promise<{ url: string | null; path: string | null; error: Error | null }> {
    try {
      // Validate file first
      const validation = this.validateFile(file);
      if (!validation.valid) {
        return { url: null, path: null, error: new Error(validation.error) };
      }

      // Security: For images, validate magic bytes and scan for dangerous content
      if (file.type.startsWith('image/')) {
        const securityCheck = await validateImageSecurity(file);
        if (!securityCheck.valid) {
          logger.warn('Unsafe image upload blocked', {
            context: { feature: 'chat', function_name: 'uploadAttachment' },
            data: { fileName: file.name, mimeType: file.type },
            allowList: ['fileName', 'mimeType'],
          });
          return { url: null, path: null, error: new Error(securityCheck.error || 'Небезопасный файл') };
        }
      }

      if (!userId) {
        return { url: null, path: null, error: new Error('User not authenticated') };
      }

      const fileName = `${safeRandomUUID()}-${file.name}`;
      const filePath = `chat/${roomId}/${userId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chat-attachments')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get signed URL valid for 1 year (31536000 seconds)
      const { data: urlData, error: urlError } = await supabase.storage
        .from('chat-attachments')
        .createSignedUrl(filePath, 31536000);

      if (urlError) throw urlError;

      return { url: urlData.signedUrl, path: filePath, error: null };
    } catch (error) {
      logger.error('Error uploading attachment', {
        context: { feature: 'chat', function_name: 'uploadAttachment' },
        error: error as Error,
        error_code: 'attachment_upload_failed',
        error_type: 'unexpected',
      });
      return { url: null, path: null, error: error as Error };
    }
  },

  /**
   * Send an image message to a chat room
   */
  async sendImageMessage(
    userId: string,
    roomId: string,
    file: File
  ): Promise<{ data: ChatMessage | null; error: Error | null }> {
    const { url, error: uploadError } = await this.uploadAttachment(userId, roomId, file);
    if (uploadError || !url) {
      return { data: null, error: uploadError || new Error('Failed to upload') };
    }

    return this.sendMessage(userId, roomId, url, 'image');
  },

  /**
   * Mark messages as read by updating last_read_at
   */
  async markAsRead(userId: string, roomId: string): Promise<{ error: Error | null }> {
    try {
      if (!userId) {
        return { error: new Error('User not authenticated') };
      }

      const { error } = await supabase
        .from('chat_participants')
        .update({ last_read_at: new Date().toISOString() })
        .eq('chat_room_id', roomId)
        .eq('user_id', userId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      logger.error('Error marking as read', {
        context: { feature: 'chat', function_name: 'markAsRead' },
        error: error as Error,
        error_code: 'mark_read_failed',
        error_type: 'unexpected',
      });
      return { error: error as Error };
    }
  },
};

export default chatMessageService;
